import { Controller } from "@hotwired/stimulus"
import cytoscape from "cytoscape"

export default class extends Controller {
  static targets = ["canvas", "tooltipDefault", "tooltipDetail", "tooltipLabel", "tooltipMeta", "filter", "loading"]
  static values = { dataUrl: String }

  async connect() {
    this.activeTypes = new Set(
      this.filterTargets
        .filter((f) => f.checked)
        .map((f) => f.dataset.nodeType)
    )

    this.themeColors = {
      cluster: "#1f2937",
      pathway: "#afbffa",
      occupation: "#00d3bb",
      essential_element: "#b70003",
      subject: "#7c3aed",
      category: "#f59e0b",
      work_activity: "#0ea5e9",
      knowledge: "#ec4899",
      skill: "#8b5cf6",
      ability: "#f97316",
      warning: "#fcb700",
      edge: "#cbd5e1",
      textDark: "#1f2937",
      textLight: "white",
      outlineDark: "#000",
      outlineLight: "#fff",
    }

    this.cy = cytoscape({
      container: this.canvasTarget,
      elements: [],
      style: this.cytoscapeStyle(),
      wheelSensitivity: 0.2,
      minZoom: 0.05,
      maxZoom: 3,
    })

    this.cy.on("mouseover", "node", (event) => this.renderTooltip(event.target))
    this.cy.on("mouseout", "node", () => this.resetTooltip())
    this.cy.on("tap", "node", (event) => this.highlightNeighborhood(event.target))
    this.cy.on("tap", (event) => {
      if (event.target === this.cy) this.clearHighlight()
    })

    await this.withLoading(() => this.refreshGraph({ initial: true }))
  }

  disconnect() {
    this.cy.destroy()
  }

  onFilterChange(event) {
    const type = event.target.dataset.nodeType
    if (event.target.checked) {
      this.activeTypes.add(type)
    } else {
      this.activeTypes.delete(type)
    }
    this.refreshGraph()
  }

  fit() {
    this.cy.fit(undefined, 40)
  }

  async relayout() {
    await this.withLoading(() => this._doLayout())
  }

  _doLayout({ resetCamera = true, animate = false } = {}) {
    const visible = this.cy.elements(":visible")
    const roots = this.rootNodes()
    const layout = this.cy.layout({ ...this.layoutOptions(roots), eles: visible, animate })
    if (resetCamera) {
      layout.one("layoutstop", () => {
        this.cy.zoom(1)
        this.cy.center(roots.nonempty() ? roots : undefined)
      })
    }
    layout.run()
  }

  rootNodes() {
    const order = ["cluster", "subject", "pathway", "category", "occupation", "essential_element", "work_activity", "knowledge", "skill", "ability"]
    for (const type of order) {
      const nodes = this.cy.nodes(`[type = "${type}"]:visible`)
      if (nodes.nonempty()) return nodes
    }
    return this.cy.collection()
  }

  async withLoading(fn) {
    this.loadingTarget.classList.remove("hidden")
    await new Promise((r) => setTimeout(r, 0))
    try {
      await fn()
    } finally {
      this.loadingTarget.classList.add("hidden")
    }
  }

  async refreshGraph({ initial = false } = {}) {
    const payload = await this._fetchPayload()
    if (!payload) return

    const incoming = this.buildElements(payload)
    const incomingNodeIds = new Set(incoming.nodes.map((n) => n.data.id))
    const incomingEdgeIds = new Set(incoming.edges.map((e) => e.data.id))

    this.cy.nodes().filter((n) => !incomingNodeIds.has(n.id())).remove()
    this.cy.edges().filter((e) => !incomingEdgeIds.has(e.id())).remove()

    const nodesToAdd = incoming.nodes.filter((n) => this.cy.getElementById(n.data.id).empty())
    const edgesToAdd = incoming.edges.filter((e) => this.cy.getElementById(e.data.id).empty())
    this.cy.add({ nodes: nodesToAdd, edges: edgesToAdd })

    if (nodesToAdd.length > 0) this._doLayout({ resetCamera: initial, animate: !initial })
  }

  async _fetchPayload() {
    if (this.abortController) this.abortController.abort()
    this.abortController = new AbortController()

    const url = new URL(this.dataUrlValue, window.location.origin)
    const types = this.activeTypes.size ? [...this.activeTypes] : [""]
    types.forEach((t) => url.searchParams.append("types[]", t))

    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: this.abortController.signal,
      })
      return await response.json()
    } catch (e) {
      if (e.name === "AbortError") return null
      throw e
    }
  }

  buildElements(payload) {
    const nodes = payload.nodes.map((n) => ({
      data: { id: n.id, label: n.label, type: n.type, code: n.code },
    }))
    const edges = payload.edges.map((e) => ({
      data: { id: e.id, source: e.source, target: e.target },
    }))
    return { nodes, edges }
  }

  layoutOptions(roots) {
    return {
      name: "breadthfirst",
      directed: true,
      roots: roots,
      spacingFactor: 1.2,
      animate: false,
      fit: false,
      padding: 60,
      transform: (node, position) => ({x: position.y, y: position.x}),
    }
  }

  cytoscapeStyle() {
    const c = this.themeColors
    return [
      {
        selector: "node",
        style: {
          label: "data(label)",
          "text-valign": "center",
          "text-halign": "center",
          "font-size": 8,
          "text-wrap": "wrap",
          "text-max-width": 70,
          color: c.textLight,
          "text-outline-color": c.outlineDark,
          "text-outline-width": 1,
          width: 18,
          height: 18,
        },
      },
      {
        selector: 'node[type = "cluster"]',
        style: {
          "background-color": c.cluster,
          width: 70,
          height: 70,
          "font-size": 14,
        },
      },
      {
        selector: 'node[type = "pathway"]',
        style: {
          "background-color": c.pathway,
          color: c.textDark,
          "text-outline-color": c.outlineLight,
          width: 40,
          height: 40,
          "font-size": 10,
        },
      },
      {
        selector: 'node[type = "occupation"]',
        style: {
          "background-color": c.occupation,
          color: c.textDark,
          "text-outline-color": c.outlineLight,
        },
      },
      {
        selector: 'node[type = "essential_element"]',
        style: {
          "background-color": c.essential_element,
        },
      },
      {
        selector: 'node[type = "subject"]',
        style: {
          "background-color": c.subject,
          width: 70,
          height: 70,
          "font-size": 14,
        },
      },
      {
        selector: 'node[type = "category"]',
        style: {
          "background-color": c.category,
          color: c.textDark,
          "text-outline-color": c.outlineLight,
          width: 30,
          height: 30,
          "font-size": 9,
        },
      },
      {
        selector: 'node[type = "work_activity"]',
        style: {
          "background-color": c.work_activity,
          width: 50,
          height: 50,
          "font-size": 12,
        },
      },
      {
        selector: 'node[type = "knowledge"]',
        style: {
          "background-color": c.knowledge,
          width: 24,
          height: 24,
          "font-size": 8,
        },
      },
      {
        selector: 'node[type = "skill"]',
        style: {
          "background-color": c.skill,
          width: 24,
          height: 24,
          "font-size": 8,
        },
      },
      {
        selector: 'node[type = "ability"]',
        style: {
          "background-color": c.ability,
          width: 24,
          height: 24,
          "font-size": 8,
        },
      },
      {
        selector: "edge",
        style: {
          width: 1,
          "line-color": c.edge,
          "target-arrow-color": c.edge,
          "target-arrow-shape": "triangle",
          "curve-style": "bezier",
          "arrow-scale": 0.7,
          opacity: 0.6,
        },
      },
      {
        selector: ".highlighted",
        style: {
          "background-color": c.warning,
          "line-color": c.warning,
          "target-arrow-color": c.warning,
          opacity: 1,
          "z-index": 99,
        },
      },
      {
        selector: ".dimmed",
        style: {
          opacity: 0.1,
        },
      },
    ]
  }

  renderTooltip(node) {
    const type = node.data("type")
    const code = node.data("code")
    this.tooltipLabelTarget.textContent = node.data("label")
    this.tooltipMetaTarget.textContent = `${formatType(type)}${code ? " · " + code : ""}`
    this.tooltipDefaultTarget.classList.add("hidden")
    this.tooltipDetailTarget.classList.remove("hidden")
  }

  resetTooltip() {
    this.tooltipDefaultTarget.classList.remove("hidden")
    this.tooltipDetailTarget.classList.add("hidden")
  }

  highlightNeighborhood(node) {
    this.cy.elements().addClass("dimmed").removeClass("highlighted")
    const neighborhood = node.closedNeighborhood()
    neighborhood.removeClass("dimmed").addClass("highlighted")
  }

  clearHighlight() {
    this.cy.elements().removeClass("dimmed").removeClass("highlighted")
  }
}

function formatType(type) {
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}
